import { useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { formatDateTime } from '../lib/format.js';

interface Change {
  id: string;
  isComment?: boolean;
  timestamp: string;
  reason: string | null;
  fromStatus: string | null;
  toStatus: string;
  actor: { displayName: string; email: string | null } | null;
}

export function OrderComments({
  orderId,
  changes,
  canComment,
  onSaved,
}: {
  orderId: string;
  changes: Change[];
  canComment: boolean;
  onSaved: () => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const { t } = i18n;
  const comments = changes.filter((c) => c.isComment);
  const start = comments[0]?.timestamp;
  const end = comments[comments.length - 1]?.timestamp;
  const timeline = changes.filter(
    (c) =>
      c.isComment === true ||
      (start && end && c.fromStatus !== c.toStatus && c.timestamp > start && c.timestamp < end),
  );
  async function save() {
    setBusy(true);
    setError(false);
    try {
      await api(`/orders/${orderId}/comments`, { method: 'POST', body: { text } });
      setText('');
      onSaved();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">{t('order.comments.title')}</h3>
      <ul className="space-y-2">
        {timeline.map((c) => (
          <li
            key={c.id}
            className={
              c.isComment
                ? 'rounded border border-border p-3 text-sm'
                : 'text-xs text-muted-foreground'
            }
          >
            {c.isComment ? (
              <>
                <div className="text-xs text-muted-foreground">
                  {c.actor?.displayName.trim() ||
                    c.actor?.email ||
                    t('order.corrections.system_actor')}{' '}
                  · {formatDateTime(c.timestamp)}
                </div>
                <p className="whitespace-pre-wrap break-words">{c.reason}</p>
              </>
            ) : (
              <>
                {c.fromStatus ? t(`order.status.${c.fromStatus}`) : '—'} →{' '}
                {t(`order.status.${c.toStatus}`)} · {formatDateTime(c.timestamp)}
              </>
            )}
          </li>
        ))}
      </ul>
      {canComment ? (
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <textarea
              aria-label={t('order.comments.title')}
              value={text}
              maxLength={500}
              onChange={(e) => setText(e.target.value)}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onDrop={(e) => e.preventDefault()}
              className="w-full rounded border border-border bg-background p-2"
            />
            <span className="text-xs text-muted-foreground">{text.length}/500</span>
          </div>
          <button
            type="button"
            disabled={busy || !text.trim()}
            onClick={() => void save()}
            className="rounded border border-border px-3 py-2 disabled:opacity-50"
          >
            {t('common.save')}
          </button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t('order.comments.closed')}</p>
      )}
      {error ? (
        <p role="alert" className="text-destructive">
          {t('common.error')}
        </p>
      ) : null}
    </section>
  );
}
